import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import { getSiteStatus } from 'services/siteStatus';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useSocket, { playAlertSound } from 'hooks/useSocket';
import './command-center.css';

// ── PLACEHOLDER DATA ──
const PLACEHOLDER = {
  agents: { actifs: 47, total: 62 },
  sites: { actifs: 23, total: 28 },
  incidents: { aujourd_hui: 3, ouverts: 2 },
  rondes_jour: { complete: 12, objectif: 24 },
};

const PLACEHOLDER_ALERTS = [
  { id: 1, level: 'critical', time: '16:02', type: 'INTRUSION', desc: 'Intrusion detectee — Site Ecobank Tokoin', site: 'Ecobank Tokoin' },
  { id: 2, level: 'warning', time: '15:45', type: 'RETARD', desc: 'Agent en retard — Poste Aeroport', site: 'Aeroport' },
  { id: 3, level: 'ok', time: '15:30', type: 'RONDE', desc: 'Ronde completee — Site Total Kegue', site: 'Total Kegue' },
  { id: 4, level: 'critical', time: '15:12', type: 'ALARME', desc: 'Alarme declenchee — Entrepot Zone Portuaire', site: 'Zone Portuaire' },
  { id: 5, level: 'warning', time: '14:58', type: 'EQUIPEMENT', desc: 'Radio hors service — Unite Mobile 3', site: 'Unite Mobile 3' },
  { id: 6, level: 'ok', time: '14:40', type: 'RELEVE', desc: 'Releve effectuee — Poste Ambassade FR', site: 'Ambassade FR' },
];

const PLACEHOLDER_ACTIVITY = [
  { id: 1, time: '16:05', action: 'Validation contrat agent', user: 'A. Mensah' },
  { id: 2, time: '15:55', action: 'Incident cree #INC-2026-089', user: 'K. Agbeko' },
  { id: 3, time: '15:42', action: 'Ronde demarree Site Orabank', user: 'M. Dogbe' },
  { id: 4, time: '15:30', action: 'Equipement affecte — Gilet #G-045', user: 'D. Agbeko' },
  { id: 5, time: '15:18', action: 'Prise de service validee', user: 'E. Adjakly' },
  { id: 6, time: '15:05', action: 'Vehicule V-012 en patrouille', user: 'S. Gnassingbe' },
  { id: 7, time: '14:50', action: 'Alarme acquittee — Zone Portuaire', user: 'A. Mensah' },
  { id: 8, time: '14:38', action: 'Formation planifiee — Secourisme', user: 'R. Lawson' },
  { id: 9, time: '14:20', action: 'Client ajoute — BSIC Togo', user: 'K. Agbeko' },
  { id: 10, time: '14:05', action: 'Rapport incident #INC-2026-088', user: 'M. Dogbe' },
];

const DEFAULT_ZOOM = 7;
const CAPITALE_ZOOM = 13;

const PAYS_COORDS = {
  'Togo': { center: [6.1375, 1.2123], ville: 'Lome', zoom: 7 },
  'Cote d\'Ivoire': { center: [5.3600, -4.0083], ville: 'Abidjan', zoom: 7 },
  'Senegal': { center: [14.6928, -17.4467], ville: 'Dakar', zoom: 7 },
  'Cameroun': { center: [3.8480, 11.5021], ville: 'Yaounde', zoom: 7 },
  'Benin': { center: [6.3654, 2.4183], ville: 'Cotonou', zoom: 7 },
  'Mali': { center: [12.6392, -8.0029], ville: 'Bamako', zoom: 7 },
  'Burkina Faso': { center: [12.3714, -1.5197], ville: 'Ouagadougou', zoom: 7 },
  'Niger': { center: [13.5137, 2.1098], ville: 'Niamey', zoom: 7 },
  'Guinee': { center: [9.6412, -13.5784], ville: 'Conakry', zoom: 7 },
  'Congo': { center: [4.2634, 15.2429], ville: 'Brazzaville', zoom: 7 },
  'Gabon': { center: [-0.7893, 11.5094], ville: 'Libreville', zoom: 7 },
  'RDC': { center: [-4.3217, 15.3222], ville: 'Kinshasa', zoom: 6 },
  'Tchad': { center: [12.1348, 15.0557], ville: 'N\'Djamena', zoom: 6 },
  'Mauritanie': { center: [18.0735, -15.9582], ville: 'Nouakchott', zoom: 6 },
  'Ghana': { center: [5.6037, -0.1870], ville: 'Accra', zoom: 7 },
  'Nigeria': { center: [6.5244, 3.3792], ville: 'Lagos', zoom: 7 },
};

const SOC_SCREENS = [
  { label: 'CCTV', status: 'EN LIGNE' },
  { label: 'ALARMES', status: 'EN LIGNE' },
  { label: 'GPS', status: 'EN LIGNE' },
  { label: 'RADIO', status: 'EN LIGNE' },
  { label: 'CYBER', status: 'EN LIGNE' },
];

function makeIcon(color) {
  return L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 6px ${color}"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  });
}

const SITE_ICONS = {
  incident: makeIcon('#e53935'),
  normal: makeIcon('#00C853'),
  sous_effectif: makeIcon('#FF9100'),
  en_attente: makeIcon('#5C7FA8'),
  audit: makeIcon('#2196F3'),
  inactif: makeIcon('#888'),
};

function getSiteIcon(site) {
  const status = getSiteStatus(site);
  if (status.color === '#888') return SITE_ICONS.inactif;
  if (status.color === '#e53935') return SITE_ICONS.incident;
  if (status.color === '#FF9100') return SITE_ICONS.sous_effectif;
  if (status.color === '#5C7FA8') return SITE_ICONS.en_attente;
  return SITE_ICONS.normal;
}

function FitBounds({ sites }) {
  const map = useMap();
  useEffect(() => {
    if (sites.length === 0) return;
    const bounds = L.latLngBounds(sites.map(s => [parseFloat(s.latitude), parseFloat(s.longitude)]));
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
  }, [sites, map]);
  return null;
}

function MapControls({ mapConfig, onFitAll }) {
  const map = useMap();
  const pays = mapConfig.pays || 'Togo';
  const paysData = PAYS_COORDS[pays] || PAYS_COORDS['Togo'];
  const center = mapConfig.carte_lat ? [mapConfig.carte_lat, mapConfig.carte_lng] : paysData.center;
  const ville = mapConfig.carte_ville || paysData.ville || 'Capitale';
  const btnStyle = { background: '#222', color: '#fff', border: '1px solid #555', borderRadius: 4, padding: '4px 8px', fontSize: 9, cursor: 'pointer', fontFamily: 'Orbitron, sans-serif', letterSpacing: 1, whiteSpace: 'nowrap' };

  return (
    <div className="leaflet-top leaflet-right" style={{ marginTop: 10, marginRight: 10 }}>
      <div className="leaflet-control" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <button onClick={() => map.setView(center, mapConfig.carte_zoom || DEFAULT_ZOOM)} style={btnStyle}>
          {pays.toUpperCase().slice(0, 5)}
        </button>
        <button onClick={() => map.setView(center, CAPITALE_ZOOM)} style={btnStyle}>
          {ville.toUpperCase().slice(0, 8)}
        </button>
        <button onClick={onFitAll} style={btnStyle}>
          SITES
        </button>
      </div>
    </div>
  );
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const months = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aout', 'Sep', 'Oct', 'Nov', 'Dec'];
  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = `${days[now.getUTCDay()]}. ${pad(now.getUTCDate())} ${months[now.getUTCMonth()]} ${now.getUTCFullYear()}`;
  const timeStr = `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`;
  return { dateStr, timeStr };
}

export default function CommandCenterPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mapSites, setMapSites] = useState([]);
  const [mapConfig, setMapConfig] = useState({});
  const [fitAll, setFitAll] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const { dateStr, timeStr } = useClock();
  const alertsPanelRef = useRef(null);
  const activityPanelRef = useRef(null);

  useEffect(() => {
    api.get('/parametres').then(res => setMapConfig(res.data || {})).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, mapRes] = await Promise.all([
        api.get('/dashboard').catch(() => ({ data: null })),
        api.get('/sites/map-data').catch(() => ({ data: [] })),
      ]);
      setData(dashRes.data);
      setMapSites(mapRes.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  // WebSocket for real-time updates
  useSocket({
    'incident:nouveau': (data) => {
      playAlertSound(data.gravite || 'moyen');
      fetchData();
    },
    'agent:statut': () => fetchData(),
    'ronde:update': () => fetchData(),
    'alerte:nouvelle': () => fetchData(),
  });

  // Close panels on outside click
  useEffect(() => {
    const handler = (e) => {
      if (showAlerts && alertsPanelRef.current && !alertsPanelRef.current.contains(e.target)) setShowAlerts(false);
      if (showActivity && activityPanelRef.current && !activityPanelRef.current.contains(e.target)) setShowActivity(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAlerts, showActivity]);

  const d = data || PLACEHOLDER;
  const agentsActifs = d.agents?.actifs || 0;
  const sitesActifs = d.sites?.actifs || 0;
  const sitesTotal = d.sites?.total || 0;
  const incidents24 = d.incidents?.aujourd_hui || 0;
  const rondesComplete = d.rondes_jour?.complete || 0;
  const rondesObjectif = d.rondes_jour?.objectif || 24;
  const alertCount = PLACEHOLDER_ALERTS.filter(a => a.level !== 'ok').length;

  const paysData = PAYS_COORDS[mapConfig.pays] || PAYS_COORDS['Togo'];
  const mapCenter = mapConfig.carte_lat ? [parseFloat(mapConfig.carte_lat), parseFloat(mapConfig.carte_lng)] : paysData.center;
  const mapZoom = mapConfig.carte_zoom || DEFAULT_ZOOM;
  const paysLabel = (mapConfig.pays || 'Togo').toUpperCase();

  return (
    <div className="cc-root">
      {/* ── HEADER ── */}
      <header className="cc-header">
        <div className="cc-header-left">
          <h1 className="cc-title">CENTRE DE COMMANDEMENT</h1>
          <div className="cc-status">
            <span className="cc-status-dot" />
            <span className="cc-status-text">OPERATIONNEL</span>
          </div>
        </div>
        <div className="cc-header-right">
          <div className="cc-clock">
            <span className="cc-clock-time">{timeStr}</span>
            <span className="cc-clock-date">{dateStr}</span>
          </div>
        </div>
      </header>

      {/* ── KPI BAR ── */}
      <div className="cc-kpi-bar">
        <div className="cc-kpi-item">
          <span className="cc-kpi-num">{agentsActifs}</span>
          <span className="cc-kpi-lbl">Agents en service</span>
        </div>
        <div className="cc-kpi-sep" />
        <div className="cc-kpi-item">
          <span className="cc-kpi-num">{sitesActifs}<span className="cc-kpi-frac">/{sitesTotal}</span></span>
          <span className="cc-kpi-lbl">Sites</span>
        </div>
        <div className="cc-kpi-sep" />
        <div className="cc-kpi-item">
          <span className="cc-kpi-num" style={incidents24 > 0 ? { color: '#FF1744' } : undefined}>{incidents24}</span>
          <span className="cc-kpi-lbl">Incidents 24h</span>
        </div>
        <div className="cc-kpi-sep" />
        <div className="cc-kpi-item">
          <span className="cc-kpi-num">{rondesComplete}<span className="cc-kpi-frac">/{rondesObjectif}</span></span>
          <span className="cc-kpi-lbl">Rondes</span>
        </div>
      </div>

      {/* ── MAP FULL WIDTH ── */}
      <div className="cc-map-full">
        <div className="cc-map-label">CARTE OPERATIONNELLE — {paysLabel}</div>
        <div className="cc-map-container">
          <MapContainer center={mapCenter} zoom={mapZoom} minZoom={5} maxZoom={18} style={{ height: '100%', width: '100%', background: '#0A0A0F' }} zoomControl={true}>
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
            />
            <MapControls mapConfig={mapConfig} onFitAll={() => setFitAll(f => !f)} />
            {fitAll && <FitBounds sites={mapSites} />}
            {mapSites.map(site => (
              <Marker key={site.id} position={[parseFloat(site.latitude), parseFloat(site.longitude)]} icon={getSiteIcon(site)}>
                <Popup>
                  <div style={{ fontSize: 12, minWidth: 180, color: '#333' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{site.nom}</div>
                    {site.client_nom && <div style={{ color: '#666', marginBottom: 4 }}>{site.client_nom}</div>}
                    <div style={{ marginBottom: 2 }}>Agents : <strong>{site.agents_presents}</strong></div>
                    <div style={{ marginBottom: 4 }}>
                      Statut : <span style={{ color: getSiteStatus(site).color, fontWeight: 600 }}>{getSiteStatus(site).label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Risque : {site.niveau_risque}</div>
                    <button onClick={() => navigate('/sites')} style={{ background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer', width: '100%' }}>
                      Voir la fiche site
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Legend */}
          <div className="cc-map-legend">
            <span><i className="cc-legend-dot" style={{ background: '#e53935' }} />Incident</span>
            <span><i className="cc-legend-dot" style={{ background: '#00C853' }} />Normal</span>
            <span><i className="cc-legend-dot" style={{ background: '#FF9100' }} />Sous-effectif</span>
            <span><i className="cc-legend-dot" style={{ background: '#888' }} />Inactif</span>
          </div>

          {/* Floating buttons — top right */}
          <div className="cc-floating-buttons">
            <button
              className={`cc-float-btn ${showAlerts ? 'cc-float-btn-active' : ''}`}
              title="Voir les alertes actives"
              onClick={(e) => { e.stopPropagation(); setShowAlerts(v => !v); setShowActivity(false); }}
            >
              <span className="cc-float-icon">🔔</span>
              <span className="cc-float-text">Alertes</span>
              {alertCount > 0 && <span className="cc-float-badge">{alertCount}</span>}
            </button>
            <button
              className={`cc-float-btn ${showActivity ? 'cc-float-btn-active' : ''}`}
              title="Activité recente"
              onClick={(e) => { e.stopPropagation(); setShowActivity(v => !v); setShowAlerts(false); }}
            >
              <span className="cc-float-icon">📋</span>
              <span className="cc-float-text">Activité</span>
            </button>
            <button
              className={`cc-float-btn ${fitAll ? 'cc-float-btn-active' : ''}`}
              title="Liste des sites surveilles"
              onClick={(e) => { e.stopPropagation(); setFitAll(f => !f); }}
            >
              <span className="cc-float-icon">📍</span>
              <span className="cc-float-text">Sites</span>
              <span className="cc-float-badge cc-float-badge-white">{sitesActifs}</span>
            </button>
          </div>

          {/* Alerts slide panel */}
          <div ref={alertsPanelRef} className={`cc-slide-panel ${showAlerts ? 'cc-slide-panel-open' : ''}`}>
            <div className="cc-slide-panel-title">ALERTES ACTIVES</div>
            <div className="cc-slide-panel-list">
              {PLACEHOLDER_ALERTS.map(al => (
                <div key={al.id} className="cc-slide-alert">
                  <span className={`cc-alert-dot cc-alert-${al.level}`} />
                  <div className="cc-slide-alert-body">
                    <div className="cc-slide-alert-head">
                      <span className="cc-slide-alert-type">{al.type}</span>
                      <span className="cc-slide-alert-time">{al.time}</span>
                    </div>
                    <div className="cc-slide-alert-desc">{al.desc}</div>
                    {al.site && <div className="cc-slide-alert-site">{al.site}</div>}
                  </div>
                  <button className="cc-slide-alert-btn">Traiter</button>
                </div>
              ))}
            </div>
          </div>

          {/* Activity slide panel */}
          <div ref={activityPanelRef} className={`cc-slide-panel cc-slide-panel-activity ${showActivity ? 'cc-slide-panel-open' : ''}`}>
            <div className="cc-slide-panel-title">ACTIVITÉ RÉCENTE</div>
            <div className="cc-slide-panel-list">
              {PLACEHOLDER_ACTIVITY.map(act => (
                <div key={act.id} className="cc-activity-item">
                  <span className="cc-activity-time">{act.time}</span>
                  <div className="cc-activity-body">
                    <div className="cc-activity-action">{act.action}</div>
                    <div className="cc-activity-user">{act.user}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── STATUS BAR ── */}
      <div className="cc-status-bar">
        {SOC_SCREENS.map(s => (
          <div key={s.label} className="cc-status-bar-item">
            <span className="cc-soc-led" />
            <span className="cc-status-bar-label">{s.label} {s.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
